import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'lib/auth';

import { type TeamQuotaUsageResponse } from '../quota-usage';

interface UseTeamQuotaUsageOptions {
  enabled?: boolean;
  /** When set, a failure raises a notification through the query error handler. */
  errorMessage?: string;
}

export const useTeamQuotaUsage = ({
  enabled = true,
  errorMessage,
}: UseTeamQuotaUsageOptions = {}) => {
  const auth = useAuth();
  const team = auth.user?.user.team;
  const canRead = team?.permissions.all.includes('read_team') === true;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['team', team?.id, 'quotas-usage'],
    queryFn: async ({ signal }): Promise<TeamQuotaUsageResponse> => {
      const res = await auth.authFetch(
        `/org/v1/teams/${team?.id ?? ''}/quotas/usage/`,
        { signal },
      );
      if (!res.ok) {
        throw new Error(res.statusText);
      }
      return res.json();
    },
    enabled: !!team?.id && canRead && enabled,
    refetchOnWindowFocus: false,
    ...(errorMessage ? { meta: { errorMessage } } : {}),
  });

  return { quotas: data?.quotas, isLoading, isError };
};
